/**
 * src/pages/Flash.jsx — flash gallery management.
 *
 * Posting a design is two API calls behind one form: the image is uploaded
 * first (uploadFile handles the presign/PUT/confirm handshake), then the
 * design record is created against the resulting key.
 *
 * The status column is worth reading carefully: an artist can mark a design
 * sold, but cannot mark it claimed — that only happens when a deposit clears
 * through Stripe. See api/src/routes/flash.js for why.
 */
import { useRef, useState } from 'react';

import { api, uploadFile } from '../lib/api.js';
import { useApi } from '../lib/useApi.js';
import { formatMoney } from '../lib/format.js';
import { useAuth } from '../lib/auth.jsx';
import { Empty, ErrorBanner, Loader, Modal, PageHeader, Pill } from '../components/ui.jsx';

export default function Flash() {
  const { artist } = useAuth();
  const [creating, setCreating] = useState(false);
  const { data, error, loading, reload } = useApi('/api/flash');
  const studio = useApi('/api/studio');

  const currency = studio.data?.studio?.settings?.currency ?? 'gbp';
  const designs = data?.flash ?? [];
  const galleryUrl = studio.data?.studio ? `/gallery/${studio.data.studio.slug}` : null;

  return (
    <>
      <PageHeader
        title="Flash"
        subtitle="Designs clients can claim with a deposit."
        actions={
          <>
            {galleryUrl ? (
              <a className="btn" href={galleryUrl} target="_blank" rel="noreferrer">
                View public gallery
              </a>
            ) : null}
            <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
              Post a design
            </button>
          </>
        }
      />

      <Loader loading={loading} error={error}>
        {designs.length === 0 ? (
          <div className="card">
            <Empty>No flash posted yet. Put something up and it appears in your public gallery.</Empty>
          </div>
        ) : (
          <div className="gallery">
            {designs.map((d) => (
              <FlashCard key={d.id} design={d} currency={currency} onChanged={reload} />
            ))}
          </div>
        )}
      </Loader>

      {creating ? (
        <PostFlash
          defaultArtistId={artist?.id}
          onClose={() => setCreating(false)}
          onCreated={() => {
            reload();
            setCreating(false);
          }}
        />
      ) : null}
    </>
  );
}

/**
 * One design tile with its management actions.
 *
 * @param {{ design: object, currency: string, onChanged: () => void }} props
 * @returns {JSX.Element}
 */
function FlashCard({ design, currency, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Runs an action and refreshes the grid.
   *
   * @param {() => Promise<unknown>} fn
   */
  const run = async (fn) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const available = design.status === 'available';

  return (
    <div className={`card flash-card${available ? '' : ' unavailable'}`} style={{ padding: 12 }}>
      <img src={design.imageUrl} alt={design.title} loading="lazy" />
      {design.status === 'sold' ? <div className="flash-sold-overlay">SOLD</div> : null}

      <div className="stack" style={{ marginTop: 10, gap: 6 }}>
        <div className="row-between">
          <strong>{design.title}</strong>
          <Pill status={design.status} />
        </div>
        <div className="muted small">
          {formatMoney(design.price, currency)} · {formatMoney(design.depositAmount, currency)} deposit
        </div>
        <div className="muted small">{design.artistName}</div>
        {design.claimedByName ? (
          <div className="small">Claimed by {design.claimedByName}</div>
        ) : null}

        <ErrorBanner>{error}</ErrorBanner>

        <div className="row wrap">
          {design.status === 'claimed' ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => run(() => api.patch(`/api/flash/${design.id}`, { status: 'sold' }))}
            >
              Mark tattooed
            </button>
          ) : null}
          {design.status === 'sold' ? (
            <button
              type="button"
              className="btn btn-sm"
              disabled={busy}
              onClick={() => run(() => api.patch(`/api/flash/${design.id}`, { status: 'available' }))}
            >
              Relist
            </button>
          ) : null}
          {available ? (
            <button
              type="button"
              className="btn btn-sm btn-danger"
              disabled={busy}
              onClick={() => run(() => api.del(`/api/flash/${design.id}`))}
            >
              Remove
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

/**
 * Upload-and-post form.
 *
 * @param {{ defaultArtistId: string, onClose: () => void, onCreated: () => void }} props
 * @returns {JSX.Element}
 */
function PostFlash({ defaultArtistId, onClose, onCreated }) {
  const artists = useApi('/api/artists');
  const fileRef = useRef(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    price: '',
    depositAmount: '',
    artistId: defaultArtistId ?? '',
  });
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError('Choose an image of the design.');
      return;
    }

    setBusy(true);
    setError(null);
    try {
      // Image first: the design record needs its key and URL.
      const upload = await uploadFile(file, { assetType: 'flash' });
      await api.post('/api/flash', {
        title: form.title,
        description: form.description || undefined,
        // Prices are entered in whole currency units and stored in minor ones.
        price: Math.round(Number(form.price) * 100),
        depositAmount: Math.round(Number(form.depositAmount) * 100),
        imageR2Key: upload.r2Key,
        imageUrl: upload.publicUrl,
        artistId: form.artistId || undefined,
      });
      onCreated();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <Modal title="Post a flash design" onClose={onClose}>
      <form className="stack" onSubmit={submit}>
        <ErrorBanner>{error}</ErrorBanner>

        <div>
          <label htmlFor="fimage">Design image</label>
          <input id="fimage" ref={fileRef} type="file" accept="image/*" required />
        </div>
        <div>
          <label htmlFor="ftitle">Title</label>
          <input id="ftitle" value={form.title} onChange={set('title')} required />
        </div>
        <div>
          <label htmlFor="fdesc">Description</label>
          <textarea
            id="fdesc"
            value={form.description}
            onChange={set('description')}
            placeholder="Size, placement, how many times it can be tattooed."
          />
        </div>
        <div className="row">
          <div className="grow">
            <label htmlFor="fprice">Price</label>
            <input
              id="fprice"
              type="number"
              min="1"
              step="0.01"
              value={form.price}
              onChange={set('price')}
              required
            />
          </div>
          <div className="grow">
            <label htmlFor="fdeposit">Deposit</label>
            <input
              id="fdeposit"
              type="number"
              min="1"
              step="0.01"
              value={form.depositAmount}
              onChange={set('depositAmount')}
              required
            />
          </div>
        </div>
        <div>
          <label htmlFor="fartist">Artist</label>
          <select id="fartist" value={form.artistId} onChange={set('artistId')}>
            {(artists.data?.artists ?? []).map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
        </div>

        <button type="submit" className="btn btn-primary" disabled={busy}>
          {busy ? 'Posting…' : 'Post design'}
        </button>
      </form>
    </Modal>
  );
}
